import { useEffect, useRef } from "react";

const activeModalIds = [];
let modalSequence = 0;

const isWeb = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

const resolveMaybeFunction = (value) =>
  typeof value === "function" ? value() : value;

const getTargetNodeName = (target) => {
  const nodeName = target?.nodeName;
  return typeof nodeName === "string" ? nodeName.toLowerCase() : "";
};

const getTargetAttribute = (target, attributeName) => {
  if (!target || typeof target?.getAttribute !== "function") {
    return null;
  }

  return target.getAttribute(attributeName);
};

const isSingleLineEditableTarget = (target) => {
  const nodeName = getTargetNodeName(target);

  if (nodeName === "input") {
    return true;
  }

  const role = getTargetAttribute(target, "role");
  if (role === "textbox") {
    return !isMultilineEditableTarget(target);
  }

  return false;
};

export const isMultilineEditableTarget = (target) => {
  const nodeName = getTargetNodeName(target);

  if (nodeName === "textarea") {
    return true;
  }

  const ariaMultiline = getTargetAttribute(target, "aria-multiline");
  if (ariaMultiline === "true") {
    return true;
  }

  const dataMultiline = getTargetAttribute(target, "data-multiline");
  if (dataMultiline === "true") {
    return true;
  }

  return Boolean(target?.isContentEditable);
};

export const resolveModalShortcutAction = (event, config = {}) => {
  const {
    onConfirm,
    onCancel,
    isConfirmEnabled = true,
    allowEnterOnSingleLineInput = true,
    allowEnterOnMultilineInput = false,
    closeOnEnterWithoutConfirm = false,
    isBlocked = false,
    isTopMost = true,
    preserveEscapeException = false,
  } = config;

  if (!resolveMaybeFunction(isTopMost)) {
    return null;
  }

  if (resolveMaybeFunction(isBlocked)) {
    return null;
  }

  const key = event?.key;

  if (key === "Escape") {
    if (preserveEscapeException) {
      return null;
    }

    return typeof onCancel === "function" ? "cancel" : null;
  }

  if (key !== "Enter") {
    return null;
  }

  if (event?.shiftKey || event?.ctrlKey || event?.metaKey || event?.altKey) {
    return null;
  }

  const target = event?.target;
  const isMultilineTarget = isMultilineEditableTarget(target);

  if (isMultilineTarget && !allowEnterOnMultilineInput) {
    return null;
  }

  if (
    !isMultilineTarget &&
    isSingleLineEditableTarget(target) &&
    !allowEnterOnSingleLineInput
  ) {
    return null;
  }

  if (typeof onConfirm === "function") {
    if (!resolveMaybeFunction(isConfirmEnabled)) {
      return null;
    }

    return "confirm";
  }

  if (closeOnEnterWithoutConfirm && typeof onCancel === "function") {
    return "cancel";
  }

  return null;
};

const registerModalId = (modalId) => {
  if (!modalId) {
    return;
  }

  const existingIndex = activeModalIds.indexOf(modalId);
  if (existingIndex !== -1) {
    activeModalIds.splice(existingIndex, 1);
  }

  activeModalIds.push(modalId);
};

const unregisterModalId = (modalId) => {
  if (!modalId) {
    return;
  }

  const index = activeModalIds.indexOf(modalId);
  if (index !== -1) {
    activeModalIds.splice(index, 1);
  }
};

const isTopModalId = (modalId) => {
  if (!modalId || activeModalIds.length === 0) {
    return false;
  }

  return activeModalIds[activeModalIds.length - 1] === modalId;
};

export const useModalKeyboardShortcuts = ({
  isVisible,
  onConfirm,
  onCancel,
  isConfirmEnabled = true,
  allowEnterOnSingleLineInput = true,
  allowEnterOnMultilineInput = false,
  closeOnEnterWithoutConfirm = false,
  isBlocked = false,
  preserveEscapeException = false,
} = {}) => {
  const modalIdRef = useRef(null);

  useEffect(() => {
    if (!isVisible || !isWeb()) {
      return undefined;
    }

    if (!modalIdRef.current) {
      modalSequence += 1;
      modalIdRef.current = `modal-shortcut-${modalSequence}`;
    }

    registerModalId(modalIdRef.current);

    return () => {
      unregisterModalId(modalIdRef.current);
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !isWeb()) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const action = resolveModalShortcutAction(event, {
        onConfirm,
        onCancel,
        isConfirmEnabled,
        allowEnterOnSingleLineInput,
        allowEnterOnMultilineInput,
        closeOnEnterWithoutConfirm,
        isBlocked,
        isTopMost: () => isTopModalId(modalIdRef.current),
        preserveEscapeException,
      });

      if (!action) {
        return;
      }

      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (action === "confirm") {
        onConfirm?.();
        return;
      }

      onCancel?.();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    allowEnterOnMultilineInput,
    allowEnterOnSingleLineInput,
    closeOnEnterWithoutConfirm,
    isBlocked,
    isConfirmEnabled,
    isVisible,
    onCancel,
    onConfirm,
    preserveEscapeException,
  ]);
};
